import { Anchor, Text } from '@mantine/core';
import { EditableText } from '@ayocore/platform/client/components/EditableText';
import { useNavigate } from 'react-router';
import { ViewPanel, InfoRow } from '@ayocore/platform/client/components/EntityViewLayout';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import { DealView } from '../../../Deals/views/DealView';
import type { LeadExtended } from '../LeadViewController';

interface LeadFinancialProps {
  lead: LeadExtended;
  canEdit?: boolean;
  onAmountSave?: (amount: number | null) => void | Promise<void>;
  onProbabilitySave?: (probability: number) => void | Promise<void>;
}

const formatCurrency = (amount: number, currency: string = 'RUB') => {
  return `${amount.toLocaleString()} ${currency}`;
};

const formatDealDate = (value: string | Date | null | undefined): string | null => {
  if (!value) return null;
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Блок финансовой информации лида: сумма, вероятность, даты закрытия.
 */
export function LeadFinancial({
  lead,
  canEdit = false,
  onAmountSave,
  onProbabilitySave,
}: LeadFinancialProps) {
  const navigate = useNavigate();
  const dealDrawer = useDrawer(DealView, {
    openInTab: props => navigate(`/crm/deals/${props.dealId}`),
  });

  const dealId = lead.deal?.id ?? lead.deal_id ?? null;
  const dealNumber = lead.deal?.number ?? null;
  const dealCreatedAt = formatDealDate(lead.deal?.created_at);

  return (
    <ViewPanel>
      <InfoRow>
        <InfoRow.TitleCol>Потенциальная сумма</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <EditableText
            value={lead.amount != null ? String(lead.amount) : ''}
            canEdit={canEdit && !!onAmountSave}
            size='sm'
            placeholder='не указано'
            className={lead.amount == null ? 'entity-view-placeholder-dimmed' : undefined}
            allowEmptySave
            regexPattern={/^\d*$/}
            formatValue={value => (value ? formatCurrency(Number(value), lead.currency) : '')}
            onSave={value => {
              const amount = value === '' ? null : Number(value);
              if (amount != null && (!Number.isSafeInteger(amount) || amount < 0)) return;
              return onAmountSave?.(amount);
            }}
          />
        </InfoRow.ValueCol>
      </InfoRow>
      <InfoRow>
        <InfoRow.TitleCol>Вероятность</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <EditableText
            value={lead.probability != null ? String(lead.probability) : ''}
            canEdit={canEdit && !!onProbabilitySave}
            size='sm'
            placeholder='не указано'
            className={lead.probability == null ? 'entity-view-placeholder-dimmed' : undefined}
            regexPattern={/^(?:|\d{1,2}(?:[.,]\d{0,2})?|100(?:[.,]0{0,2})?)$/}
            formatValue={value => (value ? `${value.replace(',', '.')}%` : '')}
            onSave={value => {
              const probability = Number(value.replace(',', '.'));
              if (!Number.isFinite(probability) || probability < 0 || probability > 100) return;
              return onProbabilitySave?.(probability);
            }}
          />
        </InfoRow.ValueCol>
      </InfoRow>
      {dealId && (
        <InfoRow>
          <InfoRow.TitleCol>Сделка</InfoRow.TitleCol>
          <InfoRow.ValueCol>
            <Anchor
              size='sm'
              href={`/crm/deals/${dealId}`}
              onClick={e => {
                e.preventDefault();
                dealDrawer.open({ dealId, isModal: true });
              }}
            >
              {dealNumber ? `Сделка №${dealNumber}` : `Сделка #${dealId}`}
              {dealCreatedAt ? ` от ${dealCreatedAt}` : ''}
            </Anchor>
          </InfoRow.ValueCol>
        </InfoRow>
      )}
      {lead.expected_close_date && (
        <InfoRow>
          <InfoRow.TitleCol>Ожидаемое закрытие</InfoRow.TitleCol>
          <InfoRow.ValueCol>
            <Text size='sm'>{new Date(lead.expected_close_date).toLocaleDateString()}</Text>
          </InfoRow.ValueCol>
        </InfoRow>
      )}
      {lead.actual_close_date && (
        <InfoRow>
          <InfoRow.TitleCol>Фактическое закрытие</InfoRow.TitleCol>
          <InfoRow.ValueCol>
            <Text size='sm'>{new Date(lead.actual_close_date).toLocaleDateString()}</Text>
          </InfoRow.ValueCol>
        </InfoRow>
      )}
    </ViewPanel>
  );
}
